import { call, put, select, takeEvery } from 'redux-saga/effects';

import camelcaseKeys from 'camelcase-keys';
import { normalize } from 'normalizr';
import * as authSelectors from 'views/../redux/reducers/auth';
import API_BASE_URL from 'views/../redux/sagas/settings/apibaseurl';
import * as alerts from 'views/../redux/utils/alerts';
import { appIntl } from 'views/../utility/context/IntlGlobalProvider';
import * as actions from '../actions';
import * as selectors from '../reducers';
import * as schemas from '../schemas';
import * as types from '../types';

/* -------------------------------------------------------------------------- */
/*                                 FETCH LIST                                 */
/* -------------------------------------------------------------------------- */
function* fetchContactsList(action) {
  const { query, operatorId, organizationId } = action.payload;
  console.log('action.payload', action.payload);
  try {
    const isAuth = yield select(authSelectors.isAuthenticated);
    if (isAuth) {
      const token = yield select(authSelectors.getAuthToken);
      const response = yield call(fetch, `${API_BASE_URL}/contact/getDropdown`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          query,
          operatorId,
          organizationId
        })
      });
      const jsonResult = yield response.json();
      if (response.status <= 300 && jsonResult.success) {
        const resultData = jsonResult.data.map((result) => camelcaseKeys(result));
        const {
          entities: { contacts },
          result //order
        } = normalize(resultData, schemas.contacts);
        yield put(actions.completeFetchingContacts(contacts, result));
      } else {
        const error = {
          errorMessage: jsonResult.message,
          errorNumber: jsonResult.number
        };
        yield put(actions.failFetchingContacts(error));
      }
    }
  } catch (err) {
    const error: any = {
      errorMessage: err?.toString(),
      errorNumber: -1
    };
    yield put(actions.failFetchingContacts(error));
  }
}

export function* watchFetchContactsList() {
  yield takeEvery(types.CONTACTS_FETCH_STARTED, fetchContactsList);
}

/* -------------------------------------------------------------------------- */
/*                                     ADD                                    */
/* -------------------------------------------------------------------------- */
function* addContact(action) {
  const contact = action.payload;
  try {
    const isAuth = yield select(authSelectors.isAuthenticated);

    if (isAuth) {
      const token = yield select(authSelectors.getAuthToken);

      const response = yield call(fetch, `${API_BASE_URL}/contact/save`, {
        method: 'POST',
        body: JSON.stringify({ ...contact, contactId: null }),
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        }
      });
      const jsonResult = yield response.json();
      if (response.status <= 300 && jsonResult.success) {
        const addedContact = camelcaseKeys(jsonResult.data);
        yield put(actions.completeAddingContact(contact.contactId, addedContact));

        alerts.showSuccessCreatedMessage({
          object: appIntl().formatMessage({
            id: 'contacts.contact'
          }),
          name: contact.contactName
        });
      } else {
        const error = {
          errorMessage: jsonResult.message,
          errorNumber: jsonResult.number
        };
        // alerts.showErrorAlertFailChanges({ error });
        yield put(actions.failAddingContact(contact.contactId, error));
      }
    }
  } catch (err) {
    const error = {
      errorMessage: err?.toString(),
      errorNumber: -1
    };
    // alerts.showErrorAlertFailChanges({ error });
    yield put(actions.failAddingContact(contact.contactId, error));
  }
}

export function* watchAddContactsStarted() {
  yield takeEvery(types.CONTACT_ADD_STARTED, addContact);
}

/* -------------------------------------------------------------------------- */
/*                                    EDIT                                    */
/* -------------------------------------------------------------------------- */
function* editContact(action) {
  const contact = action.payload;
  try {
    const isAuth = yield select(authSelectors.isAuthenticated);

    if (isAuth) {
      const token = yield select(authSelectors.getAuthToken);

      const response = yield call(fetch, `${API_BASE_URL}/contact/save`, {
        method: 'POST',
        body: JSON.stringify(contact),
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        }
      });
      const jsonResult = yield response.json();

      if (response.status <= 300 && jsonResult.success) {
        const editedContact = camelcaseKeys(jsonResult.data);
        yield put(actions.completeEditingContact(editedContact));
        yield put(actions.startFetchingContacts(null, null, null));
        alerts.showSuccessEditedMessage({
          object: appIntl().formatMessage({
            id: 'contacts.contact'
          }),
          name: contact.contactName
        });
        if (
          editedContact.isDuplicateEmail === true ||
          editedContact.isDuplicateEmail === 1 ||
          editedContact.isDuplicateEmail === '1'
        ) {
          alerts.showErrorAlertWarning({
            error: {
              errorMessage: appIntl().formatMessage({
                id: 'contacts.duplicateEmailEdited',
                defaultMessage:
                  'Este contacto reemplazó a otro con el mismo correo. El anterior fue desactivado.'
              })
            }
          });
        }
      } else {
        const error = {
          errorMessage: jsonResult.message,
          errorNumber: jsonResult.number
        };
        const oldContact = yield select((state) =>
          selectors.getContact(state.contacts, contact.contactId)
        );
        yield put(actions.failEditingContact(oldContact, error));
      }
    }
  } catch (err) {
    const error = {
      errorMessage: err?.toString(),
      errorNumber: -1
    };

    const oldContact = yield select((state) =>
      selectors.getContact(state.contacts, contact.contactId)
    );
    yield put(actions.failEditingContact(oldContact, error));
  }
}

export function* watchEditContactsStarted() {
  yield takeEvery(types.CONTACT_EDIT_STARTED, editContact);
}

/* -------------------------------------------------------------------------- */
/*                                   REMOVE                                   */
/* -------------------------------------------------------------------------- */
function* removeContact(action) {
  const contactBody = action.payload;
  const contacts = contactBody.contacts;
  try {
    const isAuth = yield select(authSelectors.isAuthenticated);
    if (isAuth) {
      const token = yield select(authSelectors.getAuthToken);

      const response = yield call(fetch, `${API_BASE_URL}/contact/delete`, {
        method: 'POST',
        body: JSON.stringify({ contacts }),
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        }
      });
      const jsonResult = yield response.json();
      if (response.status <= 300 && jsonResult.success) {
        yield put(actions.completeRemovingContact(contactBody.contacts, contactBody.contactsName));
        alerts.showSuccessDeletedMessage({
          object: appIntl().formatMessage({
            id: contactBody.contacts.length > 1 ? 'contacts.contacts2' : 'contacts.contact'
          }),
          name: contactBody.contactsName,
          multiple: contactBody.contacts.length > 1
        });
      } else {
        const error = {
          errorMessage: jsonResult.message,
          errorNumber: jsonResult.number
        };
        // alerts.showErrorAlertFailChanges({ error });
        yield put(actions.failRemovingContact(contactBody.contacts, error));
      }
    }
  } catch (err) {
    const error = {
      errorMessage: err?.toString(),
      errorNumber: -1
    };
    // alerts.showErrorAlertFailChanges({ error });
    yield put(actions.failRemovingContact(contactBody.contacts, error));
  }
}

function* removeContactSuspension(action) {
  const contactId = action.payload;
  try {
    const isAuth = yield select(authSelectors.isAuthenticated);
    if (isAuth) {
      const token = yield select(authSelectors.getAuthToken);
      const response = yield call(fetch, `${API_BASE_URL}/contact/removeSuspension`, {
        method: 'POST',
        body: JSON.stringify({ contactId }),
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        }
      });
      const jsonResult = yield response.json();

      if (response.status <= 300 && jsonResult.success) {
        yield put(actions.startFetchingContacts(null, null, null));
        alerts.showSuccessMessage({
          success: appIntl().formatMessage({ id: 'contacts.removeSuspensionSuccess' })
        });
      } else {
        const error = {
          errorMessage: jsonResult.message,
          errorNumber: jsonResult.number
        };
        yield put(actions.failRemovingContactSuspension(contactId, error));
      }
    }
  } catch (err) {
    const error = {
      errorMessage: err?.toString(),
      errorNumber: -1
    };
    yield put(actions.failRemovingContactSuspension(contactId, error));
  }
}

export function* watchRemoveContactSuspension() {
  yield takeEvery(types.CONTACT_REMOVE_SUSPENSION_STARTED, removeContactSuspension);
}

export function* watchRemoveContactsStarted() {
  yield takeEvery(types.CONTACT_REMOVE_STARTED, removeContact);
}
